/* global dataLayer */

"use strict";

import Toolbox from "./toolbox";

/**
 * Automatically add custom validation messages to forms.
 *
 * Usage examples
 *
 * - Add data-validate-form="true" to from element.
 * - Then add validation rule to, for example, form input element:
 *   data-validate="required:firstName" (or data-validate="required|firstName")
 * - Required without validation: data-validate="required".
 * - Required with length limits: data-validate="required|minLength:2|maxLength:50".
 * - Same as above but email validation: data-validate="required:email|minLength:2|maxLength:50".
 * - If you want to just validate input and not require it, you can do it like this:
 *   data-validate="kplussa"
 *
 * Valid validations:
 * - firstName: First name.
 * - lastName: Last name.
 * - minLength: Minimum length.
 * - maxLength: Maximum length.
 * - email: Email.
 * - phone: Phone number.
 * - address: Address.
 * - postalCode: Postal Code.
 * - city: City.
 * - postOffice: Post office (postitoimipaikka).
 * - kplussa: k-plussa card number.
 * - kplussaBox: k-plussa card number (alternative).
 * - birthDay: Brith day. (select)
 * - birthMonth: Brith month. (select)
 * - birthYear: Brith year. (select)
 * - consent: "Hyväksythän ehdot." (checkbox)
 * - password: Password.
 * - registrationPassword"
 * - required: Required.
 */

/**
 * Parse required and rule from data attribute string.
 *
 * @param {String} data - Data attribute value. Example: required:email
 * @returns {Object} Validation rule object.
 */
function getValidationRules(data) {
	const validation = data.split("|").map(item => item.split(":"));
	const validationRules = [];

	const checkRule = (rule) => {
		if (rule.length === 2) {
			if (rule[0] === "required") {
				validationRules.push({
					required: true,
					rule: rule[1]
				});
			} else {
				validationRules.push({
					required: false,
					rule: rule[0],
					check: rule[1]
				});
			}
		} else {
			validationRules.push({
				required: rule[0] === "required",
				rule: rule[0]
			});
		}
	};

	for (const rules of validation) {
		checkRule(rules);
	}

	return validationRules;
}

const ruleConstraints = {
	address: {
		maxLength: 35
	},
	postalCode: {
		length: 5 // Must be five numbers.
	},
	phone: {
		minLength: 6
	}
};

/**
 * Get all forms that have validation enabled.
 *
 * @param {(Element)} [formElement] - Form element.
 * @returns {Object[]} Form elements.
 */
function getFormElements(formElement) {
	const forms = formElement !== undefined ? [formElement] : document.querySelectorAll('form[data-validate-form="true"]');
	let allFormItems = [];

	for (const form of forms) {

		const formItems = Array.prototype.slice.call(
			form.querySelectorAll("[data-validate]")
		);
		if (form.dataset.validatorActive !== "true") {
			form.dataset.validatorActive = "true";
			allFormItems = allFormItems.concat(
				formItems.map(function (element) {
					return {
						rules: getValidationRules(element.dataset.validate),
						element: element
					};
				})
			);
		}
	}

	return allFormItems;
}

/**
 * Set custom validity text.
 *
 * @param {Object} elementObject Contains validation rule and element.
 */
function setCustomValidityText(elementObject) {
	const element = elementObject.element;
	if (element.validity.tooLong) {

		element.setCustomValidity("Kentän maksimpituus on " + element.maxLength + " merkkiä.");

	} else if (element.validity.tooShort) {
		if (element.type === "password") {
			element.setCustomValidity("Salasanan tulee olla vähintään " + element.minLength + " merkkiä.");
		} else {
			element.setCustomValidity("Kentän tulee olla vähintään " + element.minLength + " merkkiä.");
		}

	} else {
		if (element.validity.valueMissing && element.required) {

			// For now only one validation type is allowed in addition to length and required.
			for (const ruleObject of elementObject.rules) {

				if (ruleObject.rule === "minLength" || ruleObject === "maxLength") {
					continue;
				}

				switch (ruleObject.rule) {
					case "firstName":
						element.setCustomValidity("Etunimi on pakollinen.");
						break;

					case "lastName":
						element.setCustomValidity("Sukunimi on pakollinen.");
						break;

					case "email":
						element.setCustomValidity("Sähköposti on pakollinen.");
						break;

					case "address":
						element.setCustomValidity("Katuosoite on pakollinen.");
						break;

					case "phone":
						element.setCustomValidity("Puhelinnumero on pakollinen.");
						break;

					case "city":
						element.setCustomValidity("Kaupunki on pakollinen.");
						break;

					case "postOffice":
						element.setCustomValidity("Postitoimipaikka on pakollinen.");
						break;

					case "postalCode":
						element.setCustomValidity("Postinumero on pakollinen.");
						break;

					case "consent":
						element.setCustomValidity("Hyväksythän ehdot.");
						break;

					case "registrationPassword":
					case "password":
						element.setCustomValidity("Salasana on pakollinen.");
						break;

					default:
						element.setCustomValidity("Täytäthän tämän kentän.");
						break;
				}
			}

		} else {
			for (const ruleObject of elementObject.rules) {

				if (ruleObject.rule === "minLength" || ruleObject === "maxLength") {
					continue;
				}

				switch (ruleObject.rule) {
					case "address":
						element.setCustomValidity(
							"Tarkista osoite. Maksimissan " + ruleConstraints.address.maxLength + " merkkiä."
						);
						break;

					case "postalCode":
						element.setCustomValidity(
							"Tarkista postinumero. Esim: 12345 (ilman välilyöntejä)."
						);
						break;

					case "email":
						element.setCustomValidity("Tarkista sähköpostiosoite.");
						break;

					case "phone":
						element.setCustomValidity(
							"Tarkista puhelinnumero. Esim: 0501234567 (ilman välilyöntejä, ilman +358)."
						);
						break;

					case "kplussa":
						element.setCustomValidity(
							"Anna oikea 16-numeroinen Plussa-korttinumero."
						);
						break;

					case "kplussaBox":
						element.setCustomValidity(
							"Tarkista K-Plussa-kortin numero."
						);
						break;

					case "registrationPassword":
						element.setCustomValidity("Salasanan tulee sisältää vähintään yksi merkki ja numero.");
						break;

					default:
						element.setCustomValidity("Tarkista kentän tiedot.");
						break;
				}
			}
		}

	}

}

function plussaErrorCheckAnalytics(number) {

	if (dataLayer) {
		Toolbox.waitForFinalEvent(function () {
			dataLayer.push({
				event: "GAEvent",
				eventCategory: "Error",
				eventAction: "PlussaSerie",
				eventLabel: number.length
			});
		}, 500, "plussaErrorCheck");
	}
}


/**
 * Handle k-plussa checks.
 *
 * @param {Event} e - Event.
 * @param {boolean} splitBox - Split box element
 * @returns {string} K-Plussa string.
 */
function kplussaValidity(e, splitBox) {
	const element = e.target;

	let number = "";
	const value = String(element.value).replace(/[^0-9]/g, "").trim().substring(0, 16);
	if (splitBox === true) {

		// If someone pastes full card number.
		if ((value.substring(0, 6) === "703240") && value.length >= 16) {
			number = value;
		} else {
			number = String(element.dataset.plussaPrefix).replace(/[^0-9]/g, "").trim() + value;
		}
	} else {
		number = value;
	}

	const isValid = validatePlussaNumber(number);
	element.dataset.isValid = isValid;

	if (isValid) {
		element.removeAttribute("pattern");
	} else {
		const regex = /^\b$/; // This will never match (i.e. shows validation error).
		element.setAttribute("pattern", regex.source);
	}

	clearCustomValidityText({ element: element });

	return isValid ? number : "";
}

/**
 * Check validity on blur.
 *
 * @param {Object} elementObject Contains validation rule and element.
 */
function validateOnBlur(elementObject) {
	const element = elementObject.element;

	if (element.dataset.notified === "true") {
		element.removeAttribute("data-notified");
	} else {

		if (element.reportValidity) {
			if (element.dataset.validate === "kplussa" || element.dataset.validate === "kplussaBox") {

				let plussaNumber = "";
				if (element.dataset.validate === "kplussaBox") {
					plussaNumber = (String(element.dataset.plussaPrefix) + String(element.value)).replace(/[^0-9]/g, "").trim();
				} else {
					plussaNumber = String(element.value);
				}

				if (validatePlussaNumber(plussaNumber) === false) {
					plussaErrorCheckAnalytics(element.value);
				} else {
					Toolbox.clearEventTimer("plussaErrorCheck");
				}
			}
			const validity = element.reportValidity();
			if (!validity) {
				element.dataset.notified = true;
			} else {
				element.removeAttribute("data-notified");
			}
			element.dataset.isValid = validity;

			element.setCustomValidity("");
			element.checkValidity();
		}
	}

}

/**
 * Clear custom validation text.
 *
 * @param {Object} elementObject Contains validation rule and element.
 */
function clearCustomValidityText(elementObject) {
	const element = elementObject.element;

	element.setCustomValidity("");
	element.checkValidity();
	element.dataset.isValid = "true";

}

/**
 * Adds validation related listeners to element.
 *
 * @param {Object} elementObject Contains validation rule and element.
 */
function addFormListeners(elementObject) {

	for (const ruleObject of elementObject.rules) {
		if (ruleObject.required === true) {
			elementObject.element.required = true;
		}

		if (ruleObject.rule !== "maxLength" && ruleObject.rule !== "minLength") {

			switch (ruleObject.rule) {
				case "postalCode":
					numberInput(elementObject.element, function (inputElement) {
						return inputElement.value.replace(/[^0-9]/g, "").trim();
					});
					elementObject.element.minLength = ruleConstraints.postalCode.length;
					elementObject.element.maxLength = ruleConstraints.postalCode.length;
					elementObject.element.setAttribute("pattern", new RegExp("^\\d{" + ruleConstraints.postalCode.length + "}$").source);
					break;

				case "phone":
					numberInput(elementObject.element);
					elementObject.element.setAttribute("pattern", new RegExp("^0\\d{" + Math.max(ruleConstraints.phone.minLength - 1, 0) + ",}$").source);
					break;

				case "address":
					elementObject.element.setAttribute("pattern", new RegExp("^.{0," + ruleConstraints.address.maxLength + "}$").source);
					break;
				case "email":
					//elementObject.element.type = "email";
					elementObject.element.setAttribute("pattern", /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);
					break;
				case "registrationPassword":
					//elementObject.element.setAttribute("pattern", /^(?=^.{8,}$)(?=.*\d)(?=.*\D).*$/.source);
					elementObject.element.setAttribute("pattern", /^(?=.*\d)(?=.*\D).*$/.source);
					break;
			}

			elementObject.element.addEventListener("invalid", function () {
				setCustomValidityText(elementObject);
			});


			switch (elementObject.element.tagName) {
				case "SELECT":
					if (ruleObject.rule === "birthDay" || ruleObject.rule === "birthMonth" || ruleObject.rule === "birthYear") {
						elementObject.element.dataset.validating = true;
						elementObject.element.addEventListener("change", (e) => {
							const formRow = e.target.closest(".form-row");

							if (formRow) {
								for (const element of formRow.querySelectorAll("select[data-validate=birthDay], select[data-validate=birthMonth], select[data-validate=birthYear]")) {
									clearCustomValidityText({ element: element });
								}

							} else {
								clearCustomValidityText(elementObject);
							}
						});
					}
					break;

				default:
					break;
			}

			switch (elementObject.element.type) {
				case "checkbox":
					elementObject.element.dataset.validating = true;
					elementObject.element.addEventListener("click", function () {
						clearCustomValidityText(elementObject);
					});
					break;

				case "tel":
				case "email":
				case "password":
				case "text":
					elementObject.element.addEventListener("blur", function () {
						validateOnBlur(elementObject);
					});

					elementObject.element.dataset.validating = true;

					if (elementObject.element.type === "password" && elementObject.element.dataset.showReveal === "true") {

						const passwordWrapper = document.createElement("div");
						passwordWrapper.classList.add("password-wrapper");

						const togglePassword = document.createElement("button");
						togglePassword.classList.add("toggle-password-visibility");
						togglePassword.type = "button";
						togglePassword.textContent = "Näytä";
						togglePassword.title = "Näytä salasana";

						togglePassword.addEventListener("click", (e) => {
							const passwordVisbilityElement = e.target;
							if (passwordVisbilityElement.dataset.visible === "true") {
								elementObject.element.type = "password";
								passwordVisbilityElement.dataset.visible = false;
								passwordVisbilityElement.textContent = "Näytä";
								togglePassword.title = "Näytä salasana";
							} else {
								elementObject.element.type = "text";
								passwordVisbilityElement.dataset.visible = true;
								passwordVisbilityElement.textContent = "Piilota";
								togglePassword.title = "Piilota salasana";
							}
						});

						// Wrap password input.
						elementObject.element.parentNode.insertBefore(passwordWrapper, elementObject.element);
						passwordWrapper.appendChild(elementObject.element);

						passwordWrapper.appendChild(togglePassword);

					}

					if (ruleObject.rule === "kplussa") {
						elementObject.element.addEventListener("input", kplussaValidity);
						numberInput(elementObject.element, function (inputElement) {
							return inputElement.value.replace(/[^0-9]/g, "").trim().substring(0, 16);
						});
					} else if (ruleObject.rule === "kplussaBox") {

						const plussaFormName = elementObject.element.dataset.formName || (String(elementObject.element.name || "kplussa") + "-full-number");
						let fullPlussaNumber = elementObject.element.parentNode.querySelector("input[name=" + plussaFormName + "]");
						if (!fullPlussaNumber) {
							fullPlussaNumber = document.createElement("input");
							fullPlussaNumber.type = "hidden";
							fullPlussaNumber.name = plussaFormName;
							fullPlussaNumber.value = elementObject.element.value;
							elementObject.element.parentNode.insertBefore(fullPlussaNumber, elementObject.element.nextSibling);
						}

						numberInput(elementObject.element, plussaBoxProcessing);
						elementObject.element.addEventListener("input", (e) => {
							fullPlussaNumber.value = kplussaValidity(e, true);
						});
						// Handle prefilled.
						elementObject.element.value = plussaBoxProcessing(elementObject.element);
						fullPlussaNumber.value = kplussaValidity({ target: elementObject.element }, true);


						const wrapper = elementObject.element.closest(".plussa-input-wrapper");
						if (wrapper) {

							const observer = new MutationObserver((mutationsList, observer) => {
								for (const mutation of mutationsList) {
									switch (mutation.attributeName) {
										case "data-is-valid":
											wrapper.dataset.isValid = elementObject.element.dataset.isValid;
											break;
										default:
											break;
									}
								}
							});

							observer.observe(elementObject.element, { attributes: true, childList: false, subtree: false });

						}


					} else {
						elementObject.element.addEventListener("input", function () {
							clearCustomValidityText(elementObject);
						});
					}

					break;

				default:
					break;
			}
		} else {

			if (elementObject.element.dataset.validating !== "true") {
				elementObject.element.addEventListener("invalid", function () {
					setCustomValidityText(elementObject);
				});
				elementObject.element.addEventListener("blur", function () {
					validateOnBlur(elementObject);
				});
			}

			switch (ruleObject.rule) {
				case "minLength":
					elementObject.element.dataset.validating = true;
					elementObject.element.minLength = ruleObject.check;
					break;

				case "length":
					elementObject.element.dataset.validating = true;
					elementObject.element.minLength = ruleObject.check;
					elementObject.element.maxLength = ruleObject.check;
					break;

				case "maxLength":
					elementObject.element.dataset.validating = true;
					elementObject.element.maxLength = ruleObject.check;
					break;
			}

		}
	}
}

function validatePlussaNumber(number) {
	number = String(number).trim().replace(/\s/g, "");

	if (number === "") {
		return true;
	}

	if (new RegExp(/^\d+$/).test(number) === false) {
		return false;
	} else if (number.length !== 16) {
		return false;
	} else {
		const okBeginnings = ["703240"];
		const notOkBeginnings = ["7032400", "7032404"];
		let passed = false;

		//Check that number contains allowed sequence
		for (const sequence of okBeginnings) {
			if (number.indexOf(sequence) === 0) {
				passed = true;
			}
		}

		for (const sequence of notOkBeginnings) {
			if (number.indexOf(sequence) === 0) {
				passed = false;
			}
		}

		if (!passed) {
			return false;
		}
	}

	const numberLength = number.length;
	let total = 0;

	for (let i = 0; i < numberLength; i++) {
		let digit = number[i];

		if (i % 2 === 0) {
			digit *= 2;

			if (digit > 9) {
				digit -= 9;
			}
		}

		total = parseInt(total) + parseInt(digit);
	}

	if (total % 10 === 0) {
		return true;
	} else {
		return false;
	}
}

function numberInput(inputElement, customProcessing) {

	const checkValues = (ele, customCheck) => {
		if (customCheck) {
			ele.value = customCheck(ele);
		} else {
			ele.value = ele.value.replace(/[^0-9]/g, "").trim();
		}
	};
	inputElement.addEventListener("blur", (e) => {
		checkValues(e.target, customProcessing);
	});
	inputElement.addEventListener("change", (e) => {
		checkValues(e.target, customProcessing);
	});
	inputElement.addEventListener("input", (e) => {
		checkValues(e.target, customProcessing);
	});
}

function plussaBoxProcessing(inputElement) {

	let typed = inputElement.value.replace(/[^0-9]/g, "").trim();
	//7032407443433010

	if (typed.substring(0, 6) === "703240") {
		typed = typed.substring(6, typed.length);
	}

	typed = typed.substring(0, 10);

	const serapator = "-";

	if (typed.length >= 8) {
		typed = typed.substring(0, 7) + serapator + typed.substring(7);
	}

	if (typed.length >= 11) {
		typed = typed.substring(0, 10) + serapator + typed.substring(10);
	}

	if (typed.length >= 14) {
		typed = typed.substring(0, 13) + serapator + typed.substring(13);
	}

	return typed;


}

/**
 * Add all validation related listeners to form elements.
 * 
 * @param {Element} element - Form element.
 */
function addFormValidations(element) {
	for (const elementObject of getFormElements(element)) {
		addFormListeners(elementObject);
	}
}

export default addFormValidations;
